/**
 * External Node Component for React Flow
 *
 * Renders external Smart Asset feeds (outside the boundary).
 * Purple/violet styling to distinguish from internal elements.
 */
import { memo, useState } from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import type { ValueFlowNodeData } from "../../../../utils/schemaToReactFlow";
import { useHoverContext } from "../HoverContext";
import { NodeHoverLabel } from "./NodeHoverLabel";
import { NodeMetadataTooltip } from "./NodeMetadataTooltip";
import { ObservableBadgesContainer } from "./ObservableBadge";

/**
 * Resolve spec_icon path to actual URL
 */
const resolveSpecIconUrl = (specIcon: string): string => {
  const filename = specIcon.split("/").pop();
  return `/assets/sa-elements/${filename}`;
};

/**
 * Default oracle icon SVG
 */
const OracleIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v4m0 12v4M2 12h4m12 0h4" />
    <path d="M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
  </svg>
);

export const ExternalNodeRF = memo(({ id, data, selected }: NodeProps<ValueFlowNodeData>) => {
  const [imageError, setImageError] = useState(false);
  const { hoveredNodeId } = useHoverContext();
  const isHovered = hoveredNodeId === id;
  const isMinimal = data.display === "minimal";

  if (isMinimal) {
    // Standard nodes are 80px (2/3 of core's 120px)
    const scale = data.showLabel === false ? Math.min(data.scale ?? 1, 1) : 1;
    const iconSize = Math.max(32, Math.round(80 * scale));
    const fallbackSize = Math.max(24, Math.round(54 * scale));
    const wrapperClass =
      data.showLabel === false
        ? "flex items-center justify-center relative"
        : "flex flex-col items-center gap-2 relative";
    const hiddenHandleClass = "!opacity-0 !w-2 !h-2 !bg-violet-300";
    return (
      <div
        data-testid={`value-flow-node-external-${id}`}
        className={wrapperClass}
        style={data.showLabel === false ? { width: iconSize, height: iconSize } : undefined}
      >
        {/* Hover components */}
        <NodeHoverLabel name={data.label} description={data.description} visible={isHovered} />
        <NodeMetadataTooltip
          originalData={data.originalData}
          nodeCategory="external"
          isHovered={isHovered}
        />
        {/* All 4 target handles */}
        <Handle type="target" position={Position.Top} id="top" className={hiddenHandleClass} />
        <Handle
          type="target"
          position={Position.Right}
          id="right-in"
          className={hiddenHandleClass}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom-in"
          className={hiddenHandleClass}
        />
        <Handle type="target" position={Position.Left} id="left-in" className={hiddenHandleClass} />
        {/* All 4 source handles */}
        <Handle type="source" position={Position.Top} id="top-out" className={hiddenHandleClass} />
        <Handle type="source" position={Position.Right} id="right" className={hiddenHandleClass} />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className={hiddenHandleClass}
        />
        <Handle type="source" position={Position.Left} id="left" className={hiddenHandleClass} />

        <div className="relative flex items-center justify-center">
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="object-contain"
              style={{ width: iconSize, height: iconSize }}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="text-violet-400" style={{ width: fallbackSize, height: fallbackSize }}>
              <OracleIcon />
            </div>
          )}
          {/* Observable badges from schema ui_hint */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size="sm"
            />
          )}
        </div>
        {data.showLabel !== false && data.label.trim().length > 0 && (
          <div className="text-base text-sa-text text-center leading-tight max-w-[240px] break-words whitespace-normal font-medium">
            {data.label}
          </div>
        )}
      </div>
    );
  }

  const handleClass = "!bg-violet-400 !border-violet-600 !w-3 !h-3";

  return (
    <div
      data-testid={`value-flow-node-external-${id}`}
      className={`
          px-4 py-3 rounded-lg border-2 min-w-[180px]
          bg-gradient-to-br from-violet-500/10 to-purple-500/10
          ${selected ? "border-violet-400 shadow-lg shadow-violet-500/30" : "border-violet-500/40"}
          transition-all duration-200
        `}
    >
      {/* All 4 target handles */}
      <Handle type="target" position={Position.Top} id="top" className={handleClass} />
      <Handle type="target" position={Position.Right} id="right-in" className={handleClass} />
      <Handle type="target" position={Position.Bottom} id="bottom-in" className={handleClass} />
      <Handle type="target" position={Position.Left} id="left-in" className={handleClass} />
      {/* All 4 source handles */}
      <Handle type="source" position={Position.Top} id="top-out" className={handleClass} />
      <Handle type="source" position={Position.Right} id="right" className={handleClass} />
      <Handle type="source" position={Position.Bottom} id="bottom" className={handleClass} />
      <Handle type="source" position={Position.Left} id="left" className={handleClass} />

      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className="relative flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center text-violet-400">
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="w-6 h-6 object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <OracleIcon />
          )}
          {/* Observable badges from schema ui_hint */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size="sm"
            />
          )}
        </div>

        {/* Label and description */}
        {data.showLabel !== false && (
          <div className="flex-1 min-w-0">
            <div className="text-base font-medium text-violet-400 leading-tight break-words whitespace-normal">
              {data.label}
            </div>
            {data.description && (
              <div className="text-sm text-violet-400/70 leading-tight break-words whitespace-normal">
                {data.description}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Badge for external indicator */}
      <div className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-medium bg-primary text-violet-400 rounded-full">
        EXT
      </div>
    </div>
  );
});

ExternalNodeRF.displayName = "ExternalNodeRF";
